import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  getRoles,
  assignRolesToUser,
  deleteUser,
  getUserDetails,
  toggleUserStatus,
} from "../../../redux/Slices/UsersSlice";
import {
  FaUserShield,
  FaCheck,
  FaEye,
  FaPlus,
  FaEdit,
  FaTrash,
  FaLock,
  FaPrint,
  FaFileExcel,
  FaDownload,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../shared/hooks/TitleContext";
import i18n from "../../../i18n/i18n";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { getPdfRowStyles, chunkArray } from "../../../shared/utils.js/utils";

// PDF layout constants — derived from page dimensions, not magic numbers
const PDF_PAGE_TOTAL_HEIGHT = 1123; // A4 page px height
const PDF_OUTER_PADDING = 80; // 40px top + 40px bottom
const PDF_BORDER_INNER_PADDING = 46; // outer border(10) + outer padding(6) + inner border(4) + inner padding(28-2)
const PDF_HEADER_AREA = 86; // HeaderAce.png + marginBottom + paddingBottom
const PDF_FOOTER_CLEARANCE = 130; // paddingBottom reserved for absolute footer
const PDF_TITLE_DATE_HEIGHT = 54; // Report title (h2 ~22px) + date (~20px) + margin (~12px)
const PDF_TABLE_HEADER_HEIGHT = 30; // <thead> row height
const PDF_ROW_HEIGHT = 28; // Average <tbody> row height
const USERS_PDF_ITEMS_PER_PAGE = Math.max(
  5,
  Math.floor(
    (PDF_PAGE_TOTAL_HEIGHT -
      PDF_OUTER_PADDING -
      PDF_BORDER_INNER_PADDING -
      PDF_HEADER_AREA -
      PDF_FOOTER_CLEARANCE -
      PDF_TITLE_DATE_HEIGHT -
      PDF_TABLE_HEADER_HEIGHT) /
      PDF_ROW_HEIGHT,
  ),
);

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [showViewRolesModal, setShowViewRolesModal] = useState(false);
  const [viewRolesUser, setViewRolesUser] = useState(null);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [rolesPagination, setRolesPagination] = useState(null);
  const [roleDropdownPosition, setRoleDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 300,
  });
  const roleSearchInputRef = useRef(null);
  const roleDropdownToggleRef = useRef(null);
  const roleSearchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewUser, setPreviewUser] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [userDetailsForPrint, setUserDetailsForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [previewPage, setPreviewPage] = useState(1); // For preview pagination
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const previewContainerRef = useRef(null);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.users");
  }, [setTitle]);

  const fetchUsers = async (page = currentPage, search = searchTerm) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
      };
      if (search) params.search = search;
      const result = await dispatch(getUsers(params));
      if (getUsers.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;

        let usersList = [];
        if (Array.isArray(data?.data)) {
          usersList = data.data;
        } else if (Array.isArray(data)) {
          usersList = data;
        }
        setUsers(usersList);

        // Extract pagination from data object
        const paginationData = data?.meta || data?.pagination || null;
        setPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async (
    page = roleCurrentPage,
    search = roleSearchTerm,
  ) => {
    try {
      const params = {
        page,
        per_page: 1000,
      };
      if (search) params.search = search;
      const result = await dispatch(getRoles(params));
      if (getRoles.fulfilled.match(result)) {
        const response = result.payload;
        // Handle response structure: { success: true, data: { data: [...], current_page: 1, ... } }
        const data = response?.data || response;
        const rolesList = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setRoles(rolesList);
        // Extract pagination from data object
        const paginationData = data?.data
          ? {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              per_page: data.per_page || 10,
              total: data.total || 0,
              from: data.from || 0,
              to: data.to || 0,
            }
          : data?.meta || data?.pagination || null;
        setRolesPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  useEffect(() => {
    fetchUsers(1);
    fetchRoles(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load roles when dropdown opens or page/search changes
  useEffect(() => {
    if (roleDropdownOpen) {
      fetchRoles(roleCurrentPage, roleSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCurrentPage, roleDropdownOpen]);

  // Search roles with debounce
  useEffect(() => {
    if (roleSearchTerm !== undefined && roleDropdownOpen) {
      if (roleSearchTimeoutRef.current)
        clearTimeout(roleSearchTimeoutRef.current);
      roleSearchTimeoutRef.current = setTimeout(() => {
        setRoleCurrentPage(1);
        fetchRoles(1, roleSearchTerm);
      }, 300);
    }
    return () => {
      if (roleSearchTimeoutRef.current)
        clearTimeout(roleSearchTimeoutRef.current);
    };
  }, [roleSearchTerm, roleDropdownOpen]);

  useEffect(() => {
    fetchUsers(currentPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Update preview user data when users change and preview is open for all users
  useEffect(() => {
    if (previewUser?.all && users && users.length > 0) {
      setPreviewUser({ all: true, users: users });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, searchTerm);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleAssignRoles = user => {
    setSelectedUser(user);
    setSelectedRoles(user.roles?.map(r => r.id) || []);
    setRoleSearchTerm("");
    setRoleDropdownOpen(false);
    setShowAssignModal(true);
  };

  const handleRoleToggle = roleId => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  // Use roles directly from API (search is handled server-side)
  const displayRoles = roles || [];

  const getSelectedRolesNames = () => {
    if (selectedRoles.length === 0)
      return t("users.selectRoles", { defaultValue: "اختر الأدوار" });
    return selectedRoles
      .map(id => {
        const role = roles.find(r => r.id === id);
        return role?.name || id;
      })
      .join(", ");
  };

  const confirmAssignRoles = async () => {
    if (!selectedUser) return;
    setAssigning(true);
    try {
      // Send roles as array of IDs: { "roles": [3, 4, 1] }
      const result = await dispatch(
        assignRolesToUser({ userId: selectedUser.id, roles: selectedRoles }),
      );
      if (assignRolesToUser.fulfilled.match(result)) {
        toast.success(
          t("users.assignRoles.success", {
            defaultValue: "تم تعيين الأدوار بنجاح",
          }),
          { rtl: isRTL },
        );
        setShowAssignModal(false);
        setSelectedUser(null);
        setSelectedRoles([]);
        setRoleDropdownOpen(false);
        setRoleSearchTerm("");
        fetchUsers(currentPage, searchTerm);
      } else {
        toast.error(
          result.payload ||
            t("users.assignRoles.error", { defaultValue: "فشل تعيين الأدوار" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.assignRoles.error", { defaultValue: "فشل تعيين الأدوار" }),
        { rtl: isRTL },
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleView = userId => {
    navigate(`/users/${userId}`);
  };

  const handleAdd = () => {
    navigate("/users/add");
  };

  const handleEdit = userId => {
    navigate(`/users/edit/${userId}`);
  };

  const handleApplyPerPage = () => {
    const validValue = Math.max(1, Math.min(perPageInput, 1000));
    setPerPage(validValue);
    setCurrentPage(1);
    fetchUsers(1, searchTerm);
  };

  const handleDelete = user => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      const result = await dispatch(deleteUser(userToDelete.id));
      if (deleteUser.fulfilled.match(result)) {
        toast.success(
          t("users.delete.success", { defaultValue: "تم حذف المستخدم بنجاح" }),
          { rtl: isRTL },
        );
        setShowDeleteModal(false);
        setUserToDelete(null);
        fetchUsers(currentPage, searchTerm);
      } else {
        toast.error(
          result.payload ||
            t("users.delete.error", { defaultValue: "فشل حذف المستخدم" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.delete.error", { defaultValue: "فشل حذف المستخدم" }),
        { rtl: isRTL },
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = userId => {
    navigate(`/users/${userId}/password`);
  };

  const handleToggleStatus = async user => {
    if (user.email === "admin@albarqawy.com") {
      toast.error(
        t("users.toggleStatus.adminError", {
          defaultValue: "لا يمكن تغيير حالة المستخدم الإداري",
        }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      const result = await dispatch(toggleUserStatus(user.id));
      if (toggleUserStatus.fulfilled.match(result)) {
        toast.success(
          t("users.toggleStatus.success", {
            defaultValue: "تم تغيير الحالة بنجاح",
          }),
          { rtl: isRTL },
        );
        fetchUsers(currentPage, searchTerm);
      } else {
        toast.error(
          result.payload ||
            t("users.toggleStatus.error", { defaultValue: "فشل تغيير الحالة" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.toggleStatus.error", { defaultValue: "فشل تغيير الحالة" }),
        { rtl: isRTL },
      );
    }
  };

  // Handle preview user PDF
  const handlePreviewUser = async user => {
    try {
      const result = await dispatch(getUserDetails(user.id));
      if (getUserDetails.fulfilled.match(result)) {
        const response = result.payload;
        const details = response?.data || response;
        setUserDetailsForPrint(details);
        setPreviewUser(user);
        setIsPreviewOpen(true);
        setPreviewZoom(1.0);
        setPreviewPosition({ x: 0, y: 0 });
      } else {
        toast.error(
          result.payload ||
            t("users.details.error", {
              defaultValue: "فشل تحميل تفاصيل المستخدم",
            }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.details.error", {
            defaultValue: "فشل تحميل تفاصيل المستخدم",
          }),
        { rtl: isRTL },
      );
    }
  };

  // Handle preview all users
  const handlePreviewAll = async () => {
    try {
      setIsLoading(true);
      // Fetch all users with per_page = total
      const total = pagination?.total || 0;
      if (total === 0) {
        toast.error(
          t("users.preview.noData", {
            defaultValue: "لا توجد بيانات للمعاينة",
          }),
          { rtl: isRTL },
        );
        setIsLoading(false);
        return;
      }

      const params = {
        page: 1,
        per_page: 1000,
      };
      if (searchTerm) params.search = searchTerm;

      const result = await dispatch(getUsers(params));
      if (getUsers.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;

        let usersList = [];
        if (Array.isArray(data?.data)) {
          usersList = data.data;
        } else if (Array.isArray(data)) {
          usersList = data;
        }

        if (usersList.length === 0) {
          toast.error(
            t("users.preview.noData", {
              defaultValue: "لا توجد بيانات للمعاينة",
            }),
            { rtl: isRTL },
          );
          setIsLoading(false);
          return;
        }

        const chunks = chunkArray(usersList, USERS_PDF_ITEMS_PER_PAGE);
        setPreviewUser({
          all: true,
          users: usersList,
          allUsers: usersList,
          chunks: chunks,
          searchTerm: searchTerm,
        });
        setIsPreviewOpen(true);
        setPreviewZoom(1.0);
        setPreviewPosition({ x: 0, y: 0 });
      } else {
        toast.error(
          t("users.preview.error", { defaultValue: "فشل تحميل البيانات" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("users.preview.error", { defaultValue: "فشل تحميل البيانات" }),
        { rtl: isRTL },
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle mouse down for dragging
  const handleMouseDown = e => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - previewPosition.x,
      y: e.clientY - previewPosition.y,
    });
  };

  // Handle mouse move for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = e => {
      e.preventDefault();
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = e => {
      e.preventDefault();
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleMouseUp, { passive: false });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Handle reset to fit
  const handleResetPreview = () => {
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle print from preview
  const handlePrintFromPreview = async () => {
    if (!previewUser) return;
    setIsPreviewOpen(false);
    if (previewUser.all) {
      await handlePrintAll();
    } else {
      await handlePrintUser(previewUser);
    }
  };

  // Handle print single user
  const handlePrintUser = async user => {
    try {
      const result = await dispatch(getUserDetails(user.id));
      if (!getUserDetails.fulfilled.match(result)) {
        toast.error(
          t("users.details.error", {
            defaultValue: "فشل تحميل تفاصيل المستخدم",
          }),
          { rtl: isRTL },
        );
        return;
      }
      const response = result.payload;
      const details = response?.data || response;
      setUserDetailsForPrint(details);
      setPreviewUser(user);
      setPdfType("single");

      // Wait for DOM to update
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 200));

      setIsGeneratingPdf(true);

      const element = document.getElementById("user-pdf");
      if (!element) {
        toast.error(
          t("users.print.error", { defaultValue: "قالب الطباعة غير متوفر" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        setPdfType(null);
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: false,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 10000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById("user-pdf");
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
          }
        },
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        pdfWidth,
        pdfHeight,
        undefined,
        "FAST",
      );

      pdf.save(
        `User_${user.name || user.id}_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("users.print.success", { defaultValue: "تم تنزيل PDF بنجاح" }),
        { rtl: isRTL },
      );
    } catch (err) {
      console.error("User PDF error:", err);
      toast.error(
        t("users.print.error", { defaultValue: "حدث خطأ أثناء إنشاء PDF" }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setUserDetailsForPrint(null);
      setPdfType(null);
    }
  };

  // Handle print all users
  const handlePrintAll = async () => {
    // Store original values outside try block for finally block access
    let originalUsers = null;
    let originalPagination = null;

    try {
      setIsGeneratingPdf(true);

      // Fetch all users with per_page = total
      const total = pagination?.total || 0;
      if (total === 0) {
        toast.error(
          t("users.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const params = {
        page: 1,
        per_page: 1000,
      };
      if (searchTerm) params.search = searchTerm;

      const result = await dispatch(getUsers(params));
      if (!getUsers.fulfilled.match(result)) {
        toast.error(
          t("users.print.error", { defaultValue: "فشل تحميل البيانات" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const response = result.payload;
      const data = response?.data || response;

      let allUsersList = [];
      if (Array.isArray(data?.data)) {
        allUsersList = data.data;
      } else if (Array.isArray(data)) {
        allUsersList = data;
      }

      if (allUsersList.length === 0) {
        toast.error(
          t("users.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      setPdfType("all");
      // Update users state temporarily for print template
      originalUsers = [...users];
      originalPagination = pagination;

      const chunks = chunkArray(allUsersList, USERS_PDF_ITEMS_PER_PAGE);
      setPdfChunks(chunks);

      // Wait for state update
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update - minimal wait since all chunks are already rendered
        await new Promise(resolve => requestAnimationFrame(resolve));

        const element = document.getElementById(`users-pdf-all-${chunkIndex}`);
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(
              t("users.print.error", {
                defaultValue: "قالب الطباعة غير متوفر",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `users-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page. Since each chunk has a fixed height (1123px),
        // it should align perfectly with the PDF page height.
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(`Users_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success(
        t("users.print.success", { defaultValue: "تم تنزيل PDF بنجاح" }),
        { rtl: isRTL },
      );
    } catch (err) {
      console.error("Users PDF error:", err);
      toast.error(
        t("users.print.error", { defaultValue: "حدث خطأ أثناء إنشاء PDF" }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
      // Restore original users list
      if (originalUsers && originalUsers.length > 0) {
        setUsers(originalUsers);
      }
      // Restore original pagination if needed
      if (originalPagination) {
        setPagination(originalPagination);
      }
    }
  };

  // Handle export user to Excel
  const handleExportUserToExcel = user => {
    const rolesNames =
      user.roles && user.roles.length > 0
        ? user.roles.map(r => r.name).join(", ")
        : "—";

    const data = [
      {
        [t("labels.userName", { defaultValue: "اسم المستخدم" })]:
          user.username || "—",
        [t("labels.name", { defaultValue: "الاسم" })]: user.name || "—",
        [t("sign.email", { defaultValue: "البريد الإلكتروني" })]:
          user.email || "—",
        [t("users.roles", { defaultValue: "الأدوار" })]: rolesNames,
        [t("users.status", { defaultValue: "الحالة" })]: user.is_active
          ? t("users.active", { defaultValue: "نشط" })
          : t("users.inactive", { defaultValue: "غير نشط" }),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? "مستخدم" : "User");
    const fileName = `${isRTL ? "مستخدم" : "User"}_${user.name || user.id}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("users.export.success", { defaultValue: "تم تصدير البيانات بنجاح" }),
      { rtl: isRTL },
    );
  };

  // Handle export all users to Excel
  const handleExportAllToExcel = () => {
    if (!users || users.length === 0) {
      toast.error(
        t("users.export.noData", { defaultValue: "لا توجد بيانات للتصدير" }),
        { rtl: isRTL },
      );
      return;
    }

    const data = users.map((user, index) => {
      const rolesNames =
        user.roles && user.roles.length > 0
          ? user.roles.map(r => r.name).join(", ")
          : "—";

      return {
        م: (currentPage - 1) * 10 + (index + 1),
        [t("labels.userName", { defaultValue: "اسم المستخدم" })]:
          user.username || "—",
        [t("labels.name", { defaultValue: "الاسم" })]: user.name || "—",
        [t("sign.email", { defaultValue: "البريد الإلكتروني" })]:
          user.email || "—",
        [t("users.roles", { defaultValue: "الأدوار" })]: rolesNames,
        [t("users.status", { defaultValue: "الحالة" })]: user.is_active
          ? t("users.active", { defaultValue: "نشط" })
          : t("users.inactive", { defaultValue: "غير نشط" }),
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? "المستخدمين" : "Users");
    const fileName = `${isRTL ? "المستخدمين" : "Users"}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("users.export.success", { defaultValue: "تم تصدير البيانات بنجاح" }),
      { rtl: isRTL },
    );
  };

  useEffect(() => {
    if (roleDropdownOpen && roleSearchInputRef.current) {
      roleSearchInputRef.current.focus();
    }
  }, [roleDropdownOpen]);

  const totalPages = pagination?.last_page || 1;
  const currentPageSafe = pagination?.current_page || currentPage || 1;

  if (isLoading && users.length === 0) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {t("common.loading", { defaultValue: "جاري التحميل..." })}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "white",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
            }}
          >
            {t("sidenav.users", { defaultValue: "المستخدمين" })}
          </h2>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaEye />
              {t("users.preview.all", { defaultValue: "عرض PDF للكل" })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaDownload />
              {t("users.print.all", { defaultValue: "تنزيل PDF للكل" })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaFileExcel />
              {t("users.export.all", { defaultValue: "تصدير Excel" })}
            </button>
            <button
              onClick={handleAdd}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaPlus />
              {t("users.add", { defaultValue: "إضافة مستخدم" })}
            </button>
          </div>
        </div>
        <input
          type="text"
          placeholder={t("common.search", {
            defaultValue: isRTL ? "بحث..." : "Search...",
          })}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 15px",
            borderRadius: "8px",
            border: "1px solid #333",
            backgroundColor: "#1a1f2e",
            color: "white",
            fontSize: "14px",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            boxSizing: "border-box",
            direction: dir,
            marginBottom: "15px",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "15px",
          }}
        >
          <label
            style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
          >
            {t("common.perPage", { defaultValue: "عدد السجلات" })}:
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <button
              onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "6px 10px",
                borderRadius: "6px 0 0 6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "32px",
              }}
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max="1000"
              value={perPageInput}
              onChange={e => {
                const value = parseInt(e.target.value) || 1;
                setPerPageInput(Math.max(1, Math.min(value, 1000)));
              }}
              style={{
                width: "70px",
                padding: "6px 10px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderLeft: "none",
                borderRight: "none",
                borderRadius: 0,
                color: "white",
                fontSize: "14px",
                textAlign: "center",
              }}
            />
            <button
              onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "6px 10px",
                borderRadius: "0 6px 6px 0",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "32px",
              }}
            >
              +
            </button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "10px 16px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            {t("common.apply", { defaultValue: "تطبيق" })}
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "auto",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    width: "50px",
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("labels.userName", { defaultValue: "اسم المستخدم" })}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("labels.name", { defaultValue: "الاسم" })}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("sign.email", { defaultValue: "البريد الإلكتروني" })}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("users.roles", { defaultValue: "الأدوار" })}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("users.status", { defaultValue: "الحالة" })}
                </th>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    minWidth: "300px",
                  }}
                >
                  {t("labels.actions", { defaultValue: "الإجراءات" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {users && users.length > 0 ? (
                users.map((user, index) => (
                  <tr key={user.id} style={{ borderBottom: "1px solid #333" }}>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {/* {console.log({pagination}) } */}
                      {pagination?.total -
                        ((currentPageSafe - 1) * perPage + index + 1) +
                        1}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.username || "-"}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.name || "-"}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.email || "-"}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.roles && user.roles.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "5px",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {user.roles.slice(0, 3).map(role => (
                            <span
                              key={role.id}
                              style={{
                                backgroundColor: "#AC2000",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              }}
                            >
                              {role.translation || role.name}
                            </span>
                          ))}
                          {user.roles.length > 3 && (
                            <span
                              onClick={e => {
                                e.stopPropagation();
                                setViewRolesUser(user);
                                setShowViewRolesModal(true);
                              }}
                              style={{
                                backgroundColor: "#666",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              +{" "}
                              {t("users.viewMore", {
                                defaultValue: "عرض المزيد",
                              })}{" "}
                              ({user.roles.length - 3})
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#888" }}>-</span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        color: "white",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          color: user.is_active ? "#4CAF50" : "#f44336",
                          fontWeight: "bold",
                          fontFamily:
                            '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        }}
                      >
                        {user.is_active
                          ? t("users.active", { defaultValue: "نشط" })
                          : t("users.inactive", { defaultValue: "غير نشط" })}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          justifyContent: "center",
                          flexWrap: "nowrap",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() => handlePreviewUser(user)}
                          style={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("common.view", { defaultValue: "عرض" })}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handlePrintUser(user)}
                          disabled={isGeneratingPdf}
                          style={{
                            backgroundColor: isGeneratingPdf
                              ? "#666"
                              : "#17a2b8",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                            fontSize: "12px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                            opacity: isGeneratingPdf ? 0.6 : 1,
                          }}
                          title={t("users.print.single", {
                            defaultValue: "طباعة PDF",
                          })}
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={() => handleExportUserToExcel(user)}
                          style={{
                            backgroundColor: "#28a745",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("users.export.single", {
                            defaultValue: "تصدير Excel",
                          })}
                        >
                          <FaFileExcel />
                        </button>
                        <button
                          onClick={() => handleEdit(user.id)}
                          style={{
                            backgroundColor: "#2196F3",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("common.edit", { defaultValue: "تعديل" })}
                        >
                          <FaEdit />
                        </button>
                        {user.email !== "admin@albarqawy.com" && (
                          <>
                            <button
                              onClick={() => handleAssignRoles(user)}
                              style={{
                                backgroundColor: "#AC2000",
                                color: "white",
                                border: "none",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "32px",
                                height: "32px",
                              }}
                              title={t("users.assignRoles.button", {
                                defaultValue: "تعيين أدوار",
                              })}
                            >
                              <FaUserShield />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              style={{
                                backgroundColor: user.is_active
                                  ? "#9C27B0"
                                  : "#607D8B",
                                color: "white",
                                border: "none",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "32px",
                                height: "32px",
                              }}
                              title={
                                user.is_active
                                  ? t("users.toggleStatus.deactivate", {
                                      defaultValue: "تعطيل",
                                    })
                                  : t("users.toggleStatus.activate", {
                                      defaultValue: "تفعيل",
                                    })
                              }
                            >
                              {user.is_active ? (
                                <FaToggleOn />
                              ) : (
                                <FaToggleOff />
                              )}
                            </button>
                            <button
                              onClick={() => handleChangePassword(user.id)}
                              style={{
                                backgroundColor: "#FF9800",
                                color: "white",
                                border: "none",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "32px",
                                height: "32px",
                              }}
                              title={t("sign.changePassword", {
                                defaultValue: "تغيير كلمة المرور",
                              })}
                            >
                              <FaLock />
                            </button>
                            <button
                              onClick={() => handleDelete(user)}
                              style={{
                                backgroundColor: "#f44336",
                                color: "white",
                                border: "none",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "32px",
                                height: "32px",
                              }}
                              title={t("common.delete", {
                                defaultValue: "حذف",
                              })}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      color: "white",
                      padding: "20px",
                    }}
                  >
                    {t("labels.noData", { defaultValue: "لا توجد بيانات" })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "20px",
              paddingBottom: "20px",
            }}
          >
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
              disabled={currentPageSafe === 1}
              style={{
                padding: "8px 12px",
                backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
              }}
            >
              {t("common.previous", { defaultValue: "السابق" })}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPageSafe - 2 && page <= currentPageSafe + 2),
              )
              .map((page, idx, arr) => {
                if (idx > 0 && page - arr[idx - 1] > 1) {
                  return (
                    <React.Fragment key={`ellipsis-${page}`}>
                      <span style={{ padding: "8px", color: "white" }}>
                        ...
                      </span>
                      <button
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                          color: "white",
                          border: "1px solid #333",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: "8px 12px",
                      backgroundColor:
                        page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border: "1px solid #333",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
              }
              disabled={currentPageSafe === totalPages}
              style={{
                padding: "8px 12px",
                backgroundColor:
                  currentPageSafe === totalPages ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor:
                  currentPageSafe === totalPages ? "not-allowed" : "pointer",
              }}
            >
              {t("common.next", { defaultValue: "التالي" })}
            </button>
          </div>
        )}
      </div>

      {users.length === 0 && !isLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#888",
            backgroundColor: "#1a1f2e",
            borderRadius: "8px",
          }}
        >
          {t("labels.noData", { defaultValue: "لا توجد بيانات" })}
        </div>
      )}

      {/* Assign Roles Modal */}
      {showAssignModal && selectedUser && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setShowAssignModal(false);
            setSelectedUser(null);
            setSelectedRoles([]);
            setRoleDropdownOpen(false);
            setRoleSearchTerm("");
          }}
        >
          <div
            style={{
              backgroundColor: "#1a1f2e",
              borderRadius: "8px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              overflowX: "hidden",
              direction: dir,
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                color: "white",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              {t("users.assignRoles.title", {
                defaultValue: "تعيين أدوار للمستخدم",
              })}
              : {selectedUser.name || selectedUser.username}
            </h3>
            <div style={{ marginBottom: "20px", position: "relative" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  color: "white",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                }}
              >
                {t("users.selectRoles", { defaultValue: "اختر الأدوار" })}
              </label>
              <div
                ref={roleDropdownToggleRef}
                onClick={() => {
                  if (roleDropdownToggleRef.current) {
                    const rect =
                      roleDropdownToggleRef.current.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    const dropdownMaxHeight = 300;
                    const spaceBelow = viewportHeight - rect.bottom;
                    const spaceAbove = rect.top;

                    // Calculate optimal position
                    let top = rect.bottom;
                    let maxHeight = dropdownMaxHeight;

                    // If not enough space below, show above
                    if (
                      spaceBelow < dropdownMaxHeight &&
                      spaceAbove > spaceBelow
                    ) {
                      top =
                        rect.top - Math.min(dropdownMaxHeight, spaceAbove - 20);
                      maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 20);
                    } else {
                      maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 20);
                    }

                    setRoleDropdownPosition({
                      top: top,
                      left: rect.left,
                      width: rect.width,
                      maxHeight: maxHeight,
                    });
                  }
                  setRoleDropdownOpen(prev => !prev);
                  if (!roleDropdownOpen) {
                    setTimeout(() => {
                      if (roleSearchInputRef.current) {
                        roleSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  minHeight: "44px",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: selectedRoles.length === 0 ? "#888" : "white",
                  }}
                >
                  {getSelectedRolesNames()}
                </span>
                <span style={{ fontSize: "12px", marginLeft: "10px" }}>▼</span>
              </div>
              {roleDropdownOpen &&
                ReactDOM.createPortal(
                  <div
                    style={{
                      position: "fixed",
                      top: `${roleDropdownPosition.top}px`,
                      left: `${roleDropdownPosition.left}px`,
                      width: `${roleDropdownPosition.width}px`,
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      zIndex: 10001,
                      maxHeight: `${roleDropdownPosition.maxHeight || 300}px`,
                      overflowY: "auto",
                      overflowX: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                      marginTop: "4px",
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      ref={roleSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={roleSearchTerm}
                      onChange={e => setRoleSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "10px",
                        backgroundColor: "#2d3748",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        fontSize: "14px",
                        boxSizing: "border-box",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      }}
                    />
                    {rolesPagination && rolesPagination.last_page >= 1 && (
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #333",
                          background: "#14263b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              color: "#ccc",
                              fontSize: 11,
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("common.page", { defaultValue: "صفحة" })}{" "}
                            {rolesPagination.current_page}{" "}
                            {t("common.of", { defaultValue: "من" })}{" "}
                            {rolesPagination.last_page}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 4,
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (rolesPagination.current_page > 1) {
                                setRoleCurrentPage(1);
                              }
                            }}
                            disabled={rolesPagination.current_page === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                rolesPagination.current_page === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                rolesPagination.current_page === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                rolesPagination.current_page === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (rolesPagination.current_page > 1) {
                                setRoleCurrentPage(
                                  rolesPagination.current_page - 1,
                                );
                              }
                            }}
                            disabled={rolesPagination.current_page === 1}
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                rolesPagination.current_page === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                rolesPagination.current_page === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                rolesPagination.current_page === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ‹
                          </button>
                          {(() => {
                            const pages = [];
                            const current = rolesPagination.current_page;
                            const last = rolesPagination.last_page;
                            const showPages = 5;
                            let start = Math.max(
                              1,
                              current - Math.floor(showPages / 2),
                            );
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1)
                              start = Math.max(1, end - showPages + 1);
                            for (let i = start; i <= end; i++) pages.push(i);
                            return pages.map(page => (
                              <button
                                key={page}
                                type="button"
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setRoleCurrentPage(page);
                                }}
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor:
                                    page === current ? "#AC2000" : "#1a1f2e",
                                  color: "white",
                                  border:
                                    page === current
                                      ? "1px solid #AC2000"
                                      : "1px solid #444",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight:
                                    page === current ? "bold" : "normal",
                                  minWidth: "28px",
                                }}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                rolesPagination.current_page <
                                rolesPagination.last_page
                              ) {
                                setRoleCurrentPage(
                                  rolesPagination.current_page + 1,
                                );
                              }
                            }}
                            disabled={
                              rolesPagination.current_page ===
                              rolesPagination.last_page
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                rolesPagination.current_page <
                                rolesPagination.last_page
                              ) {
                                setRoleCurrentPage(rolesPagination.last_page);
                              }
                            }}
                            disabled={
                              rolesPagination.current_page ===
                              rolesPagination.last_page
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                rolesPagination.current_page ===
                                rolesPagination.last_page
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingBottom: "8px",
                      }}
                    >
                      {displayRoles.length > 0 ? (
                        displayRoles.map((role, index) => {
                          const isSelected = selectedRoles.includes(role.id);
                          const isLast = index === displayRoles.length - 1;
                          return (
                            <div
                              key={role.id}
                              onClick={() => handleRoleToggle(role.id)}
                              style={{
                                padding: "10px",
                                paddingBottom: isLast ? "18px" : "10px",
                                cursor: "pointer",
                                backgroundColor: isSelected
                                  ? "#2a2a2a"
                                  : "transparent",
                                color: isSelected ? "#AC2000" : "white",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid #333",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              }}
                              onMouseEnter={e => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = "#374151";
                                }
                              }}
                              onMouseLeave={e => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  border: "2px solid #AC2000",
                                  borderRadius: "4px",
                                  backgroundColor: isSelected
                                    ? "#AC2000"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {isSelected && (
                                  <FaCheck
                                    style={{ color: "white", fontSize: "12px" }}
                                  />
                                )}
                              </div>
                              <span style={{ fontSize: "14px" }}>
                                {role.translation || role.name}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            padding: "10px",
                            color: "#888",
                            textAlign: "center",
                          }}
                        >
                          {t("labels.noData", {
                            defaultValue: "لا توجد نتائج",
                          })}
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body,
                )}
              {roleDropdownOpen && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 999,
                  }}
                  onClick={() => {
                    setRoleDropdownOpen(false);
                  }}
                />
              )}
              {selectedRoles.length > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "5px",
                  }}
                >
                  {selectedRoles.map(roleId => {
                    const role = roles.find(r => r.id === roleId);
                    if (!role) return null;
                    return (
                      <span
                        key={roleId}
                        style={{
                          backgroundColor: "#AC2000",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          fontFamily:
                            '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        }}
                      >
                        {role.translation || role.name}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleRoleToggle(roleId);
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                            padding: 0,
                            marginLeft: "5px",
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUser(null);
                  setSelectedRoles([]);
                  setRoleDropdownOpen(false);
                  setRoleSearchTerm("");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                }}
              >
                {t("btns.cancel", { defaultValue: "إلغاء" })}
              </button>
              <button
                onClick={confirmAssignRoles}
                disabled={assigning}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#AC2000",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: assigning ? "not-allowed" : "pointer",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {assigning ? (
                  t("common.loading", { defaultValue: "جاري التحميل..." })
                ) : (
                  <>
                    <FaCheck />
                    {t("btns.save", { defaultValue: "حفظ" })}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
        >
          <div
            style={{
              backgroundColor: "#1a1f2e",
              borderRadius: "8px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              direction: dir,
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                color: "white",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              {t("users.delete.confirm", { defaultValue: "تأكيد الحذف" })}
            </h3>
            <p
              style={{
                color: "white",
                marginBottom: "20px",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
              }}
            >
              {t("users.delete.message", {
                defaultValue: "هل أنت متأكد من حذف المستخدم",
                name: userToDelete.name || userToDelete.email,
              })}
              : {userToDelete.name || userToDelete.email}?
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                }}
              >
                {t("btns.cancel", { defaultValue: "إلغاء" })}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: deleting ? "not-allowed" : "pointer",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                }}
              >
                {deleting
                  ? t("common.loading", { defaultValue: "جاري التحميل..." })
                  : t("btns.delete", { defaultValue: "حذف" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Roles Modal */}
      {showViewRolesModal &&
        viewRolesUser &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 99999,
            }}
            onClick={() => {
              setShowViewRolesModal(false);
              setViewRolesUser(null);
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
                overflowX: "hidden",
                direction: dir,
                zIndex: 100000,
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  color: "white",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                {t("users.viewRoles.title", { defaultValue: "أدوار المستخدم" })}
                : {viewRolesUser.name || viewRolesUser.username}
              </h3>
              {viewRolesUser.roles && viewRolesUser.roles.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {viewRolesUser.roles.map(role => (
                    <span
                      key={role.id}
                      style={{
                        backgroundColor: "#AC2000",
                        color: "white",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      }}
                    >
                      {role.translation || role.name}
                    </span>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    color: "#888",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {t("users.viewRoles.noRoles", {
                    defaultValue: "لا توجد أدوار لهذا المستخدم",
                  })}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  marginTop: "30px",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewRolesModal(false);
                    setViewRolesUser(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  }}
                >
                  {t("common.close", { defaultValue: "إغلاق" })}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* PDF Preview Modal */}
      {isPreviewOpen &&
        previewUser &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.9)",
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              direction: dir,
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
                setPreviewUser(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                padding: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #333",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "white",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                }}
              >
                {previewUser.all
                  ? t("users.preview.all", {
                      defaultValue: "معاينة PDF - جميع المستخدمين",
                    })
                  : t("users.preview.single", {
                      defaultValue: "معاينة PDF - المستخدم",
                    })}
              </h3>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button
                  onClick={() => setPreviewZoom(Math.min(2, previewZoom + 0.1))}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  +
                </button>
                <span
                  style={{
                    color: "white",
                    fontSize: "14px",
                    minWidth: "50px",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {Math.round(previewZoom * 100)}%
                </span>
                <button
                  onClick={() =>
                    setPreviewZoom(Math.max(0.1, previewZoom - 0.1))
                  }
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  −
                </button>
                <button
                  onClick={handleResetPreview}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {t("common.reset", { defaultValue: "إعادة تعيين" })}
                </button>
                <button
                  onClick={handlePrintFromPreview}
                  disabled={isGeneratingPdf}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#17a2b8",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                    opacity: isGeneratingPdf ? 0.6 : 1,
                  }}
                >
                  <FaDownload />{" "}
                  {t("users.print.download", { defaultValue: "تنزيل PDF" })}
                </button>
                <button
                  onClick={() => {
                    setIsPreviewOpen(false);
                    setPreviewUser(null);
                  }}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <div
              ref={previewContainerRef}
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "20px",
                overflow: previewZoom <= 1.0 ? "auto" : "hidden",
                flex: 1,
                display: "flex",
                alignItems: previewZoom <= 1.0 ? "flex-start" : "center",
                justifyContent: previewZoom <= 1.0 ? "flex-start" : "center",
                minHeight: 0,
                position: "relative",
                cursor:
                  previewZoom > 1.0 && isDragging
                    ? "grabbing"
                    : previewZoom > 1.0
                      ? "grab"
                      : "default",
                margin: "20px",
              }}
              onMouseDown={previewZoom > 1.0 ? handleMouseDown : undefined}
            >
              <div
                style={{
                  transform:
                    previewZoom > 1.0
                      ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                      : `scale(${previewZoom})`,
                  transformOrigin:
                    previewZoom <= 1.0 ? "top center" : "center center",
                  transition: isDragging ? "none" : "transform 0.2s ease",
                  width: previewZoom <= 1.0 ? "100%" : "794px",
                  height: "1123px",
                  userSelect: previewZoom > 1.0 ? "none" : "auto",
                  marginLeft: previewZoom <= 1.0 ? "0" : "0",
                }}
              >
                {previewUser?.all ? (
                  <>
                    {previewUser.chunks && previewUser.chunks.length > 0 ? (
                      previewUser.chunks.map((chunk, chunkIndex) => (
                        <div
                          key={chunkIndex}
                          style={{
                            marginBottom:
                              chunkIndex < previewUser.chunks.length - 1
                                ? "20px"
                                : "0",
                          }}
                        >
                          <AllUsersPreviewTemplate
                            users={chunk}
                            isRTL={isRTL}
                            t={t}
                            publicUrl={publicUrl}
                            headerLogo={headerLogo}
                            pageNumber={chunkIndex + 1}
                            totalPages={previewUser.chunks.length}
                            searchTerm={previewUser.searchTerm || ""}
                          />
                        </div>
                      ))
                    ) : (
                      <AllUsersPreviewTemplate
                        users={previewUser.users || users}
                        isRTL={isRTL}
                        t={t}
                        publicUrl={publicUrl}
                        headerLogo={headerLogo}
                        searchTerm={previewUser.searchTerm || ""}
                      />
                    )}
                  </>
                ) : (
                  <UserPreviewTemplate
                    user={previewUser}
                    userDetails={userDetailsForPrint}
                    isRTL={isRTL}
                    t={t}
                    publicUrl={publicUrl}
                    headerLogo={headerLogo}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Hidden PDF Templates for Printing */}
      {pdfType === "single" && userDetailsForPrint && previewUser && (
        <div
          id="user-pdf"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            height: "1123px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              height: "1043px",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      border: "2px solid #000",
                      borderRadius: "4px",
                      overflow: "hidden",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {(() => {
                      const details = userDetailsForPrint || previewUser;
                      return [
                        {
                          label: t("labels.name", { defaultValue: "الاسم" }),
                          value: details.name || "—",
                        },
                        {
                          label: t("sign.email", {
                            defaultValue: "البريد الإلكتروني",
                          }),
                          value: details.email || "—",
                        },
                        {
                          label: t("users.roles", { defaultValue: "الأدوار" }),
                          value:
                            details.roles && details.roles.length > 0
                              ? details.roles.map(r => r.name).join(", ")
                              : "—",
                        },
                        {
                          label: t("users.status", { defaultValue: "الحالة" }),
                          value: details.is_active
                            ? t("users.active", { defaultValue: "نشط" })
                            : t("users.inactive", { defaultValue: "غير نشط" }),
                          accent: true,
                        },
                      ]
                        .filter(row => row.value && row.value !== "—")
                        .map(row => {
                          const pdfStyles = getPdfRowStyles(isRTL);
                          return (
                            <div key={row.label} style={pdfStyles.row}>
                              <div style={pdfStyles.label}>{row.label}</div>
                              <div
                                style={{
                                  ...pdfStyles.value,
                                  fontWeight: row.accent ? "700" : "500",
                                  color: row.accent ? "#0057c2" : "#000",
                                }}
                              >
                                {row.value}
                              </div>
                            </div>
                          );
                        });
                    })()}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`users-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  minHeight: "1033px",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: isRTL ? "rtl" : "ltr",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      minHeight: 0,
                      paddingBottom: "130px",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "8px",
                        textAlign: "center",
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    >
                      <h2
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#000080",
                          fontFamily:
                            '"Times New Roman Bold", "Times New Roman", serif',
                        }}
                      >
                        {isRTL ? "تقرير المستخدمين" : "Users Report"}
                      </h2>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "11px",
                          color: "#333",
                          fontFamily: '"Times New Roman", serif',
                        }}
                      >
                        {new Date().toLocaleDateString(
                          isRTL ? "ar-SA" : "en-US",
                          { year: "numeric", month: "long", day: "numeric" },
                        )}
                      </p>
                      {searchTerm && (
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "10px",
                            color: "#555",
                            fontFamily: '"Times New Roman", serif',
                          }}
                        >
                          {isRTL ? "بحث" : "Search"}: {searchTerm}
                        </p>
                      )}
                    </div>
                    <div
                      style={{
                        marginTop: "6px",
                        direction: isRTL ? "rtl" : "ltr",
                        fontFamily: '"Times New Roman", "Times", serif',
                        flex: 1,
                        overflow: "hidden",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "11px",
                          border: "1px solid #000",
                          fontFamily: '"Times New Roman", "Times", serif',
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                                width: "36px",
                              }}
                            >
                              #
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("labels.userName", {
                                defaultValue: "اسم المستخدم",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("labels.name", { defaultValue: "الاسم" })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("sign.email", {
                                defaultValue: "البريد الإلكتروني",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("users.roles", { defaultValue: "الأدوار" })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "center",
                                fontWeight: "700",
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                              }}
                            >
                              {t("users.status", { defaultValue: "الحالة" })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((user, index) => {
                            const serialNumber =
                              chunkIndex * USERS_PDF_ITEMS_PER_PAGE + index + 1;
                            return (
                              <tr
                                key={user.id}
                                style={{
                                  backgroundColor:
                                    index % 2 === 0
                                      ? "rgba(255, 255, 255, 0.7)"
                                      : "rgba(255, 255, 255, 0.5)",
                                }}
                              >
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                    width: "36px",
                                  }}
                                >
                                  {serialNumber}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {user.username || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {user.name || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                    wordBreak: "break-all",
                                    maxWidth: "100px",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {user.email || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {user.roles && user.roles.length > 0
                                    ? user.roles.map(r => r.name).join(", ")
                                    : "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    backgroundColor:
                                      index % 2 === 0
                                        ? "rgba(255, 255, 255, 0.7)"
                                        : "rgba(255, 255, 255, 0.5)",
                                  }}
                                >
                                  {user.is_active
                                    ? t("users.active", { defaultValue: "نشط" })
                                    : t("users.inactive", {
                                        defaultValue: "غير نشط",
                                      })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

// User Preview Template Component
const UserPreviewTemplate = ({
  user,
  userDetails,
  isRTL,
  t,
  publicUrl,
  headerLogo,
}) => {
  if (!user) return null;
  const details = userDetails || user;

  return (
    <div
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          height: "1043px",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
                paddingBottom: "120px",
              }}
            >
              <div
                style={{
                  marginTop: "12px",
                  border: "2px solid #000",
                  borderRadius: "4px",
                  overflow: "hidden",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                {[
                  {
                    label: t("labels.userName", {
                      defaultValue: "اسم المستخدم",
                    }),
                    value: details.username || "—",
                  },
                  {
                    label: t("labels.name", { defaultValue: "الاسم" }),
                    value: details.name || "—",
                  },
                  {
                    label: t("sign.email", {
                      defaultValue: "البريد الإلكتروني",
                    }),
                    value: details.email || "—",
                  },
                  {
                    label: t("users.roles", { defaultValue: "الأدوار" }),
                    value:
                      details.roles && details.roles.length > 0
                        ? details.roles.map(r => r.name).join(", ")
                        : "—",
                  },
                  {
                    label: t("users.status", { defaultValue: "الحالة" }),
                    value: details.is_active
                      ? t("users.active", { defaultValue: "نشط" })
                      : t("users.inactive", { defaultValue: "غير نشط" }),
                    accent: true,
                  },
                ]
                  .filter(row => row.value && row.value !== "—")
                  .map(row => {
                    const pdfStyles = getPdfRowStyles(isRTL);
                    return (
                      <div key={row.label} style={pdfStyles.row}>
                        <div style={pdfStyles.label}>{row.label}</div>
                        <div
                          style={{
                            ...pdfStyles.value,
                            fontWeight: row.accent ? "700" : "500",
                            color: row.accent ? "#0057c2" : "#000",
                          }}
                        >
                          {row.value}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "18px",
                right: "18px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// All Users Preview Template Component
const AllUsersPreviewTemplate = ({
  users,
  isRTL,
  t,
  publicUrl,
  headerLogo,
  pageNumber,
  totalPages,
  searchTerm,
}) => {
  if (!users || users.length === 0) return null;

  return (
    <div
      style={{
        width: "794px",
        height: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          height: "1043px",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: "rtl",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                paddingBottom: "130px",
              }}
            >
              <div
                style={{
                  marginTop: "8px",
                  textAlign: "center",
                  direction: isRTL ? "rtl" : "ltr",
                }}
              >
                <h2
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#000080",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", serif',
                  }}
                >
                  {isRTL ? "تقرير المستخدمين" : "Users Report"}
                </h2>
                <p
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "11px",
                    color: "#333",
                    fontFamily: '"Times New Roman", serif',
                  }}
                >
                  {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                {searchTerm && (
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "10px",
                      color: "#555",
                      fontFamily: '"Times New Roman", serif',
                    }}
                  >
                    {isRTL ? "بحث" : "Search"}: {searchTerm}
                  </p>
                )}
              </div>
              <div style={{ marginTop: "6px", flex: 1, overflow: "hidden" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    border: "1px solid #000",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "rgba(227, 242, 253, 0.4)",
                        borderBottom: "2px solid #000",
                      }}
                    >
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                          width: "36px",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("labels.userName", { defaultValue: "اسم المستخدم" })}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("labels.name", { defaultValue: "الاسم" })}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("sign.email", { defaultValue: "البريد الإلكتروني" })}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("users.roles", { defaultValue: "الأدوار" })}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #000",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t("users.status", { defaultValue: "الحالة" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => {
                      const globalIndex =
                        pageNumber && totalPages
                          ? (pageNumber - 1) * USERS_PDF_ITEMS_PER_PAGE +
                            index +
                            1
                          : index + 1;
                      return (
                        <tr
                          key={user.id}
                          style={{
                            borderBottom: "1px solid #000",
                            backgroundColor: "rgba(255, 255, 255, 0.3)",
                          }}
                        >
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                              width: "36px",
                            }}
                          >
                            {globalIndex}
                          </td>
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                            }}
                          >
                            {user.username || "—"}
                          </td>
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                            }}
                          >
                            {user.name || "—"}
                          </td>
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                              wordBreak: "break-all",
                              maxWidth: "100px",
                              whiteSpace: "normal",
                            }}
                          >
                            {user.email || "—"}
                          </td>
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                            }}
                          >
                            {user.roles && user.roles.length > 0
                              ? user.roles.map(r => r.name).join(", ")
                              : "—"}
                          </td>
                          <td
                            style={{
                              padding: "6px",
                              border: "1px solid #000",
                              textAlign: "center",
                            }}
                          >
                            {user.is_active
                              ? t("users.active", { defaultValue: "نشط" })
                              : t("users.inactive", {
                                  defaultValue: "غير نشط",
                                })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "18px",
                right: "18px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
